import React, { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import {
  FILE_SIZE_LIMITS,
  FILE_UPLOAD_ERROR,
  ONE_MB_IN_BYTES,
} from "../../helpers/constants";
import {
  getDefaultFileTypes,
  getHelperText,
  getValueOrBlank,
} from "../../helpers/utils";
import { MuiBox } from "../box/MuiBox";
import { MuiButtonTertiary } from "../button/MuiButton";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import HtmlTypography from "../htmlTypography/HtmlTypography";
import { WarningConfirmationModal } from "../modal/WarningModal";
import { MuiTypography } from "../typography/MuiTypography";
import { FileUploadWrapper } from "./FileUpload.style";
import FileUploadPreview from "./FileUploadPreview";

interface FileUploaderProps {
  formNameKey: string;
  label?: string;
  isMandatory?: boolean;
  isDropZoneHidden?: boolean;
  isCloseIconHidden?: boolean;
  clearUploadedFile?: boolean;
  onDocumentChange?: (date: Date | null) => void;
  initialFile?: File | null;
  handleFileRejection?: (isAdded: boolean) => void;
  onUploadError?: (error: string | null) => void;
  isFromBulkCreate?: boolean;
  maxFileSize?: number;
  acceptedFileTypes?: Record<string, string[]>;
  maxFileSizeErrorMessageText: string;
  fileFormatErrorMessageText: string;
  sizeLabel: string;
  fileTypeLabel?: string;
  replaceModalLabel?: string;
  browseLabel: string;
  dropYourFileLabel: string;
  replaceModalTitle?: string;
  replaceModalPrimaryButtonLabel?: string;
  replaceModalSecondaryButtonLabel?: string;
  browseImage: {
    src: string;
    alt?: string;
  };
}

const FileUploader: React.FC<FileUploaderProps> = ({
  formNameKey,
  label,
  isMandatory = false,
  isDropZoneHidden = false,
  isCloseIconHidden = false,
  clearUploadedFile = false,
  onDocumentChange,
  initialFile,
  handleFileRejection,
  onUploadError,
  isFromBulkCreate,
  acceptedFileTypes = getDefaultFileTypes(),
  maxFileSize = FILE_SIZE_LIMITS.TEN_MB,
  maxFileSizeErrorMessageText,
  fileFormatErrorMessageText,
  sizeLabel,
  fileTypeLabel,
  replaceModalLabel,
  browseLabel,
  dropYourFileLabel,
  replaceModalTitle,
  replaceModalPrimaryButtonLabel,
  replaceModalSecondaryButtonLabel,
  browseImage,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    setValue,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    setUploadedFile(initialFile ?? null);
  }, [initialFile]);

  useEffect(() => {
    if (onUploadError) {
      onUploadError(uploadError);
    }
  }, [uploadError, onUploadError]);

  useEffect(() => {
    if (clearUploadedFile && uploadedFile) {
      isFromBulkCreate ? clearUploadedFileBulkCreate() : handleRemoveFile();
    }
  }, [clearUploadedFile]);

  const clearUploadedFileBulkCreate = () => {
    setUploadedFile(null);
    setValue(formNameKey, null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setValue(formNameKey, null, { shouldValidate: true });
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onDocumentChange) onDocumentChange(null);
    if (handleFileRejection) handleFileRejection(false);
  };
  const handleAcceptFile = (file: File) => {
    setUploadedFile(file);
    setValue(formNameKey, file, { shouldValidate: true });
    setUploadError(null);
    onDocumentChange?.(new Date());
  };

  const onDrop = (acceptedFiles: File[], fileRejections: any) => {
    if (fileRejections.length > 0) {
      handleFileRejection?.(true);

      const errorMessage = fileRejections[0]?.errors[0]?.message || "";
      if (errorMessage.includes(FILE_UPLOAD_ERROR.largerFile)) {
        setUploadError(maxFileSizeErrorMessageText);
      } else if (errorMessage.includes(FILE_UPLOAD_ERROR.fileType)) {
        setUploadError(fileFormatErrorMessageText);
      }

      setUploadedFile(null);
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      if (isFromBulkCreate && uploadedFile) {
        setPendingFile(file);
        setShowReplaceModal(true);
      } else {
        handleAcceptFile(file);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxFileSize * ONE_MB_IN_BYTES,
    accept: acceptedFileTypes,
  });

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const renderError = (errorMessage: string) => (
    <MuiBox className="error-container">
      <ErrorCrossIcon />
      <MuiTypography variant="h5Bold" className="error">
        {getHelperText(errorMessage, label ?? "")}
      </MuiTypography>
    </MuiBox>
  );

  const renderDropzoneContent = () => (
    <MuiBox className="dropzone-content">
      <MuiBox className="upload-image-class">
        <img
          src={browseImage.src}
          alt={browseImage.alt ?? ""}
          className="upload-image"
        />
      </MuiBox>
      <MuiBox className="dropzone-content-title">
        <HtmlTypography
          variant="h3SemiBold"
          html={dropYourFileLabel}
          className="dropzone-label"
        />
        <MuiButtonTertiary
          className="browse-button"
          onClick={handleBrowseClick}
        >
          <HtmlTypography
            variant="h4Bold"
            html={browseLabel}
            className="browse-label"
          />
        </MuiButtonTertiary>
      </MuiBox>
      <HtmlTypography variant="h3" html={getValueOrBlank(sizeLabel)} />

      {!isFromBulkCreate && (
        <HtmlTypography variant="h3" html={getValueOrBlank(fileTypeLabel)} />
      )}
    </MuiBox>
  );

  const shouldShowDropzone = isFromBulkCreate
    ? !isDropZoneHidden
    : !isDropZoneHidden && !uploadedFile;

  return (
    <FileUploadWrapper>
      <MuiBox
        className="file-upload-container"
        data-testid="file-upload-container"
      >
        {label && (
          <MuiBox className="label">
            <MuiTypography variant="h3">{label}</MuiTypography>
            {isMandatory && (
              <MuiTypography className="asterisk">*</MuiTypography>
            )}
          </MuiBox>
        )}

        {shouldShowDropzone && (
          <MuiBox {...getRootProps()} className="dropzone">
            <input {...getInputProps()} ref={fileInputRef} />
            {isDragActive ? (
              <MuiTypography variant="body1">
                Drop the files here...
              </MuiTypography>
            ) : (
              renderDropzoneContent()
            )}
          </MuiBox>
        )}

        {uploadedFile && (
          <FileUploadPreview
            file={uploadedFile}
            showCloseIcon={!isCloseIconHidden}
            onFileRemove={handleRemoveFile}
            isFromBulkCreate={isFromBulkCreate}
          />
        )}
      </MuiBox>

      {uploadError
        ? renderError(uploadError)
        : !uploadedFile &&
          formNameKey &&
          errors[formNameKey] &&
          renderError(errors[formNameKey]?.message?.toString() ?? "")}

      {isFromBulkCreate && showReplaceModal && (
        <WarningConfirmationModal
          modalTitle={replaceModalTitle}
          labelContent={getValueOrBlank(replaceModalLabel)}
          show={showReplaceModal}
          onClose={() => {
            setShowReplaceModal(false);
            setPendingFile(null);
          }}
          onPrimaryButtonClick={() => {
            if (pendingFile) {
              handleAcceptFile(pendingFile);
            }
            setShowReplaceModal(false);
            setPendingFile(null);
          }}
          primaryButtonTitle={replaceModalPrimaryButtonLabel}
          secondaryButtonTitle={replaceModalSecondaryButtonLabel}
        />
      )}
    </FileUploadWrapper>
  );
};

export default FileUploader;
